import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../../lib/supabaseClient';
import Spinner from '../../Atoms/Spinner';
import toast from 'react-hot-toast';
import { SearchPotentialMatches, SearchPotentialProjects, GetProjectThumbnails } from '../../../api';

const Container = styled.div`
    max-width: 1600px;
    margin: 0 auto;
    padding: 40px 20px;
`;

const Header = styled.div`
    margin-bottom: 40px;

    h1 {
        font-size: 32px;
        margin-bottom: 8px;
        color: #333;
    }

    .subtitle {
        font-size: 18px;
        color: #666;
    }
`;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #f5f5f5;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    color: #333;
    transition: all 0.2s ease;
    margin-bottom: 20px;

    &:hover {
        background: #e8e8e8;
        transform: translateX(-4px);
    }
`;

const StatsSection = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 20px;
    margin-bottom: 40px;
`;

const StatCard = styled.div`
    background: ${props => props.color || 'white'};
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);

    .value {
        font-size: 36px;
        font-weight: 700;
        color: ${props => props.textColor || '#333'};
    }

    .label {
        font-size: 14px;
        color: ${props => props.textColor ? `${props.textColor}aa` : '#666'};
        margin-top: 8px;
    }
`;

const TabsContainer = styled.div`
    display: flex;
    gap: 4px;
    margin-bottom: 24px;
    background: #f0f0f0;
    border-radius: 12px;
    padding: 4px;
    width: fit-content;
`;

const Tab = styled.button`
    padding: 10px 24px;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    background: ${props => props.active ? 'white' : 'transparent'};
    color: ${props => props.active ? '#333' : '#666'};
    box-shadow: ${props => props.active ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'};

    &:hover {
        color: #333;
    }
`;

const TableSection = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    overflow: hidden;
`;

const TableHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #eee;
    flex-wrap: wrap;
    gap: 16px;

    h2 {
        font-size: 20px;
        font-weight: 600;
        margin: 0;
    }
`;

const SearchInput = styled.input`
    padding: 10px 16px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
    width: 300px;

    &:focus {
        outline: none;
        border-color: #f39c12;
    }
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;

    th {
        text-align: left;
        padding: 16px 12px;
        background: #f8f9fa;
        font-weight: 600;
        font-size: 13px;
        color: #555;
        border-bottom: 2px solid #eee;
    }

    td {
        padding: 12px;
        border-bottom: 1px solid #f0f0f0;
        font-size: 13px;
        color: #333;
    }

    tr:hover td {
        background: #f8f9fa;
    }
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;

    &.onboarded {
        background: #d4edda;
        color: #155724;
    }

    &.pending {
        background: #fff3cd;
        color: #856404;
    }
`;

const InvitationBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 5px 10px;
    border-radius: 16px;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;

    &.on-platform {
        background: #d4edda;
        color: #155724;
    }

    &.token-used {
        background: #cce5ff;
        color: #004085;
    }

    &.token-generated {
        background: #fff3cd;
        color: #856404;
    }

    &.no-token {
        background: #f8f9fa;
        color: #6c757d;
    }
`;

const CompletenessBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 5px 10px;
    border-radius: 16px;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;

    &.complete {
        background: #d4edda;
        color: #155724;
    }
    &.partial {
        background: #fff3cd;
        color: #856404;
    }
    &.none {
        background: #f8f9fa;
        color: #6c757d;
    }
`;

const CompletenessDetail = styled.span`
    font-size: 10px;
    color: #888;
    display: block;
    margin-top: 2px;
`;

const EditionBadge = styled.span`
    display: inline-block;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    background: ${props => {
        switch (props.order) {
            case 1: return '#e3f2fd';
            case 2: return '#fce4ec';
            case 3: return '#e8f5e9';
            default: return '#f5f5f5';
        }
    }};
    color: ${props => {
        switch (props.order) {
            case 1: return '#1565c0';
            case 2: return '#c62828';
            case 3: return '#2e7d32';
            default: return '#666';
        }
    }};
`;

const ActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: ${props => props.variant === 'primary' ? '#f39c12' : '#e7f3ff'};
    color: ${props => props.variant === 'primary' ? 'white' : '#004085'};
    border: none;
    border-radius: 6px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        opacity: 0.9;
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const RefreshButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #f39c12;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s ease;

    &:hover {
        background: #e67e22;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const ExportButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #28a745;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s ease;
    margin-left: 12px;

    &:hover {
        background: #218838;
    }
`;

const LastUpdate = styled.div`
    font-size: 13px;
    color: #888;
    margin-top: 16px;
    text-align: right;
    padding-right: 24px;
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: white;
    border-radius: 16px;
    padding: 24px;
    max-width: 600px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #eee;

    h3 {
        margin: 0;
        font-size: 18px;
        color: #333;
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #999;
    padding: 0;
    line-height: 1;

    &:hover {
        color: #333;
    }
`;

const FormGroup = styled.div`
    margin-bottom: 16px;

    label {
        display: block;
        font-size: 13px;
        font-weight: 600;
        color: #555;
        margin-bottom: 6px;
    }

    input, textarea {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 14px;
        transition: border-color 0.2s;

        &:focus {
            outline: none;
            border-color: #f39c12;
        }
    }

    textarea {
        min-height: 80px;
        resize: vertical;
    }
`;

const SaveButton = styled.button`
    width: 100%;
    padding: 12px;
    background: linear-gradient(135deg, #f39c12, #e67e22);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 16px;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(243, 156, 18, 0.4);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`;

const MatchButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: ${props => props.hasMatches ? '#17a2b8' : '#6c757d'};
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const MatchBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: ${props => props.count > 0 ? '#d1ecf1' : '#f8f9fa'};
    color: ${props => props.count > 0 ? '#0c5460' : '#6c757d'};
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
`;

const MatchModalContent = styled.div`
    background: white;
    border-radius: 16px;
    padding: 24px;
    max-width: 700px;
    width: 90%;
    max-height: 85vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const MatchList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 16px;
`;

const MatchCard = styled.div`
    padding: 16px;
    border: 2px solid ${props => props.selected ? '#f39c12' : '#e9ecef'};
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    background: ${props => props.selected ? '#fff8e1' : 'white'};

    &:hover {
        border-color: #f39c12;
        background: #fffcf5;
    }

    .match-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 8px;
    }

    .match-name {
        font-weight: 600;
        font-size: 15px;
        color: #333;
    }

    .match-company {
        font-size: 13px;
        color: #666;
        margin-top: 2px;
    }

    .match-email {
        font-size: 13px;
        color: #004085;
        margin-top: 4px;
    }

    .match-date {
        font-size: 11px;
        color: #999;
        background: #f8f9fa;
        padding: 2px 8px;
        border-radius: 10px;
    }
`;

const MatchInfo = styled.div`
    background: #e7f3ff;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 13px;
    color: #004085;

    strong {
        display: block;
        margin-bottom: 4px;
    }
`;

const NoMatchesMessage = styled.div`
    text-align: center;
    padding: 32px;
    color: #666;
    font-size: 14px;
`;

const SearchingIndicator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 24px;
    color: #666;
    font-size: 14px;
`;

const ProjectMatchButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: ${props => props.hasProject ? '#28a745' : props.hasMatches ? '#17a2b8' : '#6c757d'};
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ProjectMatchBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: ${props => props.linked ? '#d4edda' : '#f8f9fa'};
    color: ${props => props.linked ? '#155724' : '#6c757d'};
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
`;

const ProjectMatchCard = styled.div`
    padding: 16px;
    border: 2px solid ${props => props.selected ? '#28a745' : '#e9ecef'};
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    background: ${props => props.selected ? '#e8f5e9' : 'white'};
    display: flex;
    gap: 16px;

    &:hover {
        border-color: #28a745;
        background: #f1f8e9;
    }

    .project-thumbnail {
        width: 120px;
        height: 90px;
        background-size: cover;
        background-position: center;
        background-color: #f8f9fa;
        border-radius: 8px;
        flex-shrink: 0;
        border: 1px solid #eee;
    }

    .project-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    .project-name {
        font-weight: 600;
        font-size: 15px;
        color: #333;
        margin-bottom: 4px;
    }

    .project-company {
        font-size: 13px;
        color: #666;
        margin-bottom: 4px;
    }

    .project-type {
        font-size: 12px;
        color: #999;
        background: #f8f9fa;
        padding: 2px 8px;
        border-radius: 10px;
        display: inline-block;
    }
`;

const ConfirmProjectButton = styled.button`
    width: 100%;
    padding: 12px;
    background: linear-gradient(135deg, #28a745, #20c997);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 16px;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`;

const ProjectThumbnailSmall = styled.div`
    width: 40px;
    height: 30px;
    background-size: cover;
    background-position: center;
    background-color: #f0f0f0;
    border-radius: 4px;
    border: 1px solid #ddd;
    display: inline-block;
    vertical-align: middle;
    margin-right: 8px;
`;

const ProjectDetailGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 20px;

    @media (max-width: 500px) {
        grid-template-columns: 1fr;
    }
`;

const ProjectDetailField = styled.div`
    label {
        display: block;
        font-size: 11px;
        font-weight: 600;
        color: #888;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 4px;
    }

    .value {
        font-size: 14px;
        color: #333;
        word-break: break-word;
    }

    .empty {
        font-size: 13px;
        color: #ccc;
        font-style: italic;
    }

    &.full-width {
        grid-column: 1 / -1;
    }
`;

const ProjectDetailMedias = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 8px;

    img {
        width: 100px;
        height: 75px;
        object-fit: cover;
        border-radius: 6px;
        border: 1px solid #eee;
        cursor: pointer;
        transition: transform 0.2s;

        &:hover {
            transform: scale(1.05);
        }
    }
`;

const ProjectStatusDot = styled.span`
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 6px;
    background: ${props => props.ok ? '#28a745' : '#dc3545'};
`;

const TabSeparator = styled.div`
    width: 1px;
    background: #ccc;
    margin: 4px 8px;
`;

const CampaignSummary = styled.div`
    padding: 14px 20px;
    background: #f8f9fa;
    border-radius: 10px;
    margin-bottom: 20px;
    font-size: 14px;
    color: #333;
    display: flex;
    gap: 24px;
    flex-wrap: wrap;
    align-items: center;

    strong {
        font-weight: 700;
    }

    .sep {
        color: #ccc;
    }
`;

// ============================================================
// CONSTANTS
// ============================================================

const EDITIONS = {
    'ec25d885-3dcf-455c-93b2-26041cb842e4': { name: '1ere edition', shortName: '1ere', order: 1 },
    '417b2020-721a-4c55-bd9f-d86a913f26ad': { name: '2eme edition', shortName: '2eme', order: 2 },
    'a3b7c9d1-e5f6-4a2b-8c3d-1e5f6a2b8c3d': { name: '3eme edition', shortName: '3eme', order: 3 },
};

const EDITION_IDS = Object.keys(EDITIONS);

// ============================================================
// HELPERS
// ============================================================

function getInvitationStatus(winner, tokenByEmail) {
    if (winner.user_id) return 'on-platform';
    const email = (winner.email || '').toLowerCase().trim();
    const token = tokenByEmail[email];
    if (!token) return 'no-token';
    if (token.used) return 'token-used';
    return 'token-generated';
}

function getInvitationLabel(status) {
    switch (status) {
        case 'on-platform': return 'Sur la plateforme';
        case 'token-used': return 'Inscrit via invitation';
        case 'token-generated': return 'Token genere';
        case 'no-token': return 'Pas de token';
        default: return '-';
    }
}

function formatDate(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
}

function getProjectThumbnailUrl(thumbnailPath) {
    if (!thumbnailPath) return null;
    return process.env.REACT_APP_MEDIA_BASE_URL + thumbnailPath;
}

// ============================================================
// COMPONENT
// ============================================================

const FD100AllEditionsDashboard = () => {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [allWinners, setAllWinners] = useState([]);
    const [tokens, setTokens] = useState([]);
    const [tokenByEmail, setTokenByEmail] = useState({});
    const [activeTab, setActiveTab] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [lastUpdate, setLastUpdate] = useState(null);

    // Edit modal
    const [editModal, setEditModal] = useState(null);
    const [saving, setSaving] = useState(false);

    // Company matching
    const [matchModal, setMatchModal] = useState(null);
    const [potentialMatchesCache, setPotentialMatchesCache] = useState({});

    // Project matching
    const [projectMatchModal, setProjectMatchModal] = useState(null);
    const [potentialProjectsCache, setPotentialProjectsCache] = useState({});

    // Project detail modal
    const [projectDetailModal, setProjectDetailModal] = useState(null);

    // Thumbnails & project completeness
    const [thumbnailMap, setThumbnailMap] = useState({});
    const [completenessMap, setCompletenessMap] = useState({});

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Fetch winners and tokens in parallel
            const [winnersRes, tokensRes] = await Promise.all([
                supabase
                    .from('winners')
                    .select('*')
                    .in('event_id', EDITION_IDS)
                    .order('company_name', { ascending: true }),
                supabase
                    .from('fd100_invitation_tokens')
                    .select('*')
            ]);

            if (winnersRes.error) throw winnersRes.error;

            const winnersData = winnersRes.data || [];
            const tokensData = tokensRes.data || [];

            // Build token map
            const tMap = {};
            tokensData.forEach(t => {
                tMap[(t.email || '').toLowerCase().trim()] = t;
            });

            // Enrich winners with edition, invitation status, tier, and token data
            const enriched = winnersData.map(w => {
                const email = (w.email || '').toLowerCase().trim();
                const token = tMap[email];
                let tier = 'none';
                if (token) {
                    if (token.purpose === 'signup') tier = 'A';
                    else if (token.purpose === 'onboarding' && !w.project_id) tier = 'B';
                    else if (token.purpose === 'onboarding' && w.project_id) tier = 'C';
                }
                return {
                    ...w,
                    _edition: EDITIONS[w.event_id],
                    _invitationStatus: getInvitationStatus(w, tMap),
                    _tier: tier,
                    _token: token || null
                };
            });

            setAllWinners(enriched);
            setTokens(tokensData);
            setTokenByEmail(tMap);
            setLastUpdate(new Date());

            // Fetch thumbnails for linked projects
            const linkedItems = enriched
                .filter(w => w.project_id && w.company_id)
                .map(w => ({ project_id: w.project_id, company_id: w.company_id }));

            if (linkedItems.length > 0) {
                try {
                    const thumbResponse = await GetProjectThumbnails(linkedItems);
                    const data = thumbResponse.data || {};
                    // New format: { thumbnails: {...}, completeness: {...} }
                    if (data.thumbnails) {
                        setThumbnailMap(data.thumbnails);
                        setCompletenessMap(data.completeness || {});
                    } else {
                        // Backward compat: old format was just the thumbnail map
                        setThumbnailMap(data);
                    }
                } catch (e) {
                    console.error('Error fetching thumbnails:', e);
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Erreur lors du chargement des donnees');
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchData();
        setRefreshing(false);
        toast.success('Donnees actualisees');
    };

    // ============================================================
    // FILTERING
    // ============================================================

    const isCampaignTab = ['tierA', 'tierB', 'tierC'].includes(activeTab);
    const isActivityTab = activeTab === 'activity';
    const isEditionTab = !isCampaignTab && !isActivityTab;

    const getFilteredWinners = () => {
        let filtered = allWinners;

        // Tab filter
        if (isCampaignTab) {
            const tierKey = activeTab.replace('tier', '');
            filtered = filtered.filter(w => w._tier === tierKey);
        } else if (activeTab !== 'all' && isEditionTab) {
            filtered = filtered.filter(w => w.event_id === activeTab);
        }

        // Search filter
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(w =>
                w.first_name?.toLowerCase().includes(search) ||
                w.last_name?.toLowerCase().includes(search) ||
                w.company_name?.toLowerCase().includes(search) ||
                w.project_name?.toLowerCase().includes(search) ||
                w.email?.toLowerCase().includes(search)
            );
        }

        return filtered;
    };

    // ============================================================
    // STATS
    // ============================================================

    const getStats = () => {
        const winners = activeTab === 'all' ? allWinners : allWinners.filter(w => w.event_id === activeTab);
        const total = winners.length;
        const onPlatform = winners.filter(w => w.user_id).length;
        const withProject = winners.filter(w => w.project_id).length;
        const tokensGenerated = winners.filter(w => w._invitationStatus === 'token-generated' || w._invitationStatus === 'token-used').length;
        const signupsCompleted = winners.filter(w => w._invitationStatus === 'token-used').length;
        const projectsComplete = winners.filter(w => {
            if (!w.project_id) return false;
            const c = completenessMap[w.project_id];
            return c && c.hasImage && c.hasDescription && c.hasTypology;
        }).length;

        return { total, onPlatform, withProject, tokensGenerated, signupsCompleted, projectsComplete };
    };

    // ============================================================
    // ACTIVITY LOG
    // ============================================================

    const getActivityEvents = () => {
        const events = [];
        const eventLabels = { token_created: 'Token genere', account_created: 'Compte cree', project_linked: 'Projet lie', project_complete: 'Projet complet' };

        tokens.forEach(t => {
            const email = (t.email || '').toLowerCase().trim();
            const winner = allWinners.find(w => (w.email || '').toLowerCase().trim() === email);
            events.push({ type: 'token_created', date: t.created_at, email: t.email, name: winner ? `${winner.first_name || ''} ${winner.last_name || ''}`.trim() : '', company: winner?.company_name || '', detail: t.purpose === 'signup' ? 'Tier A' : 'Tier B/C' });
            if (t.used && t.used_at) {
                events.push({ type: 'account_created', date: t.used_at, email: t.email, name: winner ? `${winner.first_name || ''} ${winner.last_name || ''}`.trim() : '', company: winner?.company_name || '' });
            }
        });

        allWinners.filter(w => w.project_id && w.user_id).forEach(w => {
            const c = completenessMap[w.project_id];
            const isComplete = c && c.hasImage && c.hasDescription && c.hasTypology;
            events.push({ type: isComplete ? 'project_complete' : 'project_linked', date: w.updated_at, email: w.email, name: `${w.first_name || ''} ${w.last_name || ''}`.trim(), company: w.company_name || '', detail: w.project_name || '' });
        });

        events.sort((a, b) => new Date(b.date) - new Date(a.date));
        return { events, eventLabels };
    };

    // Simple campaign summary text
    const getCampaignSummaryText = (tier) => {
        const winners = allWinners.filter(w => w._tier === tier);
        const total = winners.length;
        const withAccount = winners.filter(w => w.user_id).length;
        const withProject = winners.filter(w => w.project_id).length;
        const complete = winners.filter(w => {
            const c = completenessMap[w.project_id];
            return c && c.hasImage && c.hasDescription && c.hasTypology;
        }).length;

        if (tier === 'A') return `${total} emails envoyes · ${withAccount} comptes crees (${total ? Math.round(withAccount/total*100) : 0}%) · ${withProject} projets lies · ${complete} complets`;
        if (tier === 'B') return `${total} emails envoyes · ${withProject} projets lies (${total ? Math.round(withProject/total*100) : 0}%) · ${complete} complets`;
        if (tier === 'C') return `${total} laureats concernes · ${complete} projets complets (${total ? Math.round(complete/total*100) : 0}%)`;
        return '';
    };

    // ============================================================
    // PROJECT DETAIL MODAL
    // ============================================================

    const openProjectDetail = (winner) => {
        const c = completenessMap[winner.project_id];
        if (!c) return;
        setProjectDetailModal({
            winner,
            project: c,
            thumbnail: thumbnailMap[winner.project_id]
        });
    };

    // ============================================================
    // EDIT MODAL
    // ============================================================

    const openEditModal = (winner) => setEditModal({ ...winner });
    const closeEditModal = () => setEditModal(null);
    const handleEditChange = (field, value) => setEditModal(prev => ({ ...prev, [field]: value }));

    const handleSaveEdit = async () => {
        if (!editModal) return;
        setSaving(true);
        try {
            const { error } = await supabase
                .from('winners')
                .update({
                    project_name: editModal.project_name,
                    company_name: editModal.company_name,
                    first_name: editModal.first_name,
                    last_name: editModal.last_name,
                    email: editModal.email,
                    phone: editModal.phone
                })
                .eq('id', editModal.id);

            if (error) {
                toast.error('Erreur lors de la sauvegarde');
            } else {
                toast.success('Laureat mis a jour');
                closeEditModal();
                fetchData();
            }
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    // ============================================================
    // COMPANY MATCHING
    // ============================================================

    const searchForMatches = async (winner) => {
        if (!winner.company_name || winner.user_id) return;

        if (potentialMatchesCache[winner.id]) {
            setMatchModal({ winner, matches: potentialMatchesCache[winner.id], searching: false, selectedMatch: null });
            return;
        }

        setMatchModal({ winner, matches: [], searching: true, selectedMatch: null });

        try {
            const response = await SearchPotentialMatches(winner.company_name);
            const matches = response.data?.matches || [];
            setPotentialMatchesCache(prev => ({ ...prev, [winner.id]: matches }));
            setMatchModal(prev => ({ ...prev, matches, searching: false }));
        } catch (error) {
            toast.error('Erreur lors de la recherche de correspondances');
            setMatchModal(prev => ({ ...prev, searching: false }));
        }
    };

    const handleSelectMatch = (match) => {
        setMatchModal(prev => ({
            ...prev,
            selectedMatch: prev.selectedMatch?.user_id === match.user_id ? null : match
        }));
    };

    const handleConfirmMatch = async () => {
        if (!matchModal?.winner || !matchModal?.selectedMatch) return;
        setSaving(true);
        try {
            const { error } = await supabase
                .from('winners')
                .update({
                    email: matchModal.selectedMatch.email.toLowerCase(),
                    user_id: matchModal.selectedMatch.user_id
                })
                .eq('id', matchModal.winner.id);

            if (error) {
                toast.error('Erreur: ' + error.message);
                return;
            }

            toast.success(`Laureat lie a ${matchModal.selectedMatch.first_name || ''} ${matchModal.selectedMatch.last_name || ''}`);
            setPotentialMatchesCache(prev => { const c = { ...prev }; delete c[matchModal.winner.id]; return c; });
            setMatchModal(null);
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de la liaison');
        } finally {
            setSaving(false);
        }
    };

    // ============================================================
    // PROJECT MATCHING
    // ============================================================

    const searchForProjects = async (winner) => {
        if (!winner.project_name || winner.project_id) return;

        if (potentialProjectsCache[winner.id]) {
            setProjectMatchModal({ winner, matches: potentialProjectsCache[winner.id], searching: false, selectedMatch: null });
            return;
        }

        setProjectMatchModal({ winner, matches: [], searching: true, selectedMatch: null });

        try {
            const response = await SearchPotentialProjects(winner.project_name, winner.company_name);
            const matches = response.data?.matches || [];
            setPotentialProjectsCache(prev => ({ ...prev, [winner.id]: matches }));
            setProjectMatchModal(prev => ({ ...prev, matches, searching: false }));
        } catch (error) {
            toast.error('Erreur lors de la recherche de projets');
            setProjectMatchModal(prev => ({ ...prev, searching: false }));
        }
    };

    const handleSelectProjectMatch = (match) => {
        setProjectMatchModal(prev => ({
            ...prev,
            selectedMatch: prev.selectedMatch?.project_id === match.project_id ? null : match
        }));
    };

    const handleConfirmProjectMatch = async () => {
        if (!projectMatchModal?.winner || !projectMatchModal?.selectedMatch) return;
        setSaving(true);
        try {
            const { error } = await supabase
                .from('winners')
                .update({
                    project_id: projectMatchModal.selectedMatch.project_id,
                    company_id: projectMatchModal.selectedMatch.company_id
                })
                .eq('id', projectMatchModal.winner.id);

            if (error) {
                toast.error('Erreur: ' + error.message);
                return;
            }

            toast.success(`Projet lie: ${projectMatchModal.selectedMatch.project_name}`);
            setPotentialProjectsCache(prev => { const c = { ...prev }; delete c[projectMatchModal.winner.id]; return c; });
            setProjectMatchModal(null);
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de la liaison du projet');
        } finally {
            setSaving(false);
        }
    };

    // ============================================================
    // CSV EXPORT
    // ============================================================

    const exportToCSV = () => {
        const filtered = getFilteredWinners();
        const headers = ['Edition', 'Projet', 'Entreprise', 'Prenom', 'Nom', 'Email', 'Telephone', 'Sur plateforme', 'Projet lie', 'Statut invitation'];

        const rows = filtered.map(w => [
            w._edition?.name || '',
            w.project_name || '',
            w.company_name || '',
            w.first_name || '',
            w.last_name || '',
            w.email || '',
            w.phone || '',
            w.user_id ? 'Oui' : 'Non',
            w.project_id ? 'Oui' : 'Non',
            getInvitationLabel(w._invitationStatus)
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        const tabLabel = activeTab === 'all' ? 'toutes_editions' : EDITIONS[activeTab]?.shortName || 'export';
        link.download = `fd100_dashboard_${tabLabel}_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        toast.success('Export CSV telecharge');
    };

    // ============================================================
    // RENDER
    // ============================================================

    if (loading) return <Container><Spinner /></Container>;

    const stats = getStats();
    const filteredWinners = getFilteredWinners();

    return (
        <Container>
            <BackButton onClick={() => navigate('/admin/events')}>
                ← Retour aux evenements
            </BackButton>

            <Header>
                <h1>FD100 — Tableau de bord toutes editions</h1>
                <div className="subtitle">
                    Suivi des laureats des 3 editions passees du French Design 100
                </div>
            </Header>

            {/* Edition tabs: show global stats */}
            {isEditionTab && (
                <StatsSection>
                    <StatCard>
                        <div className="value">{stats.total}</div>
                        <div className="label">Laureats total</div>
                    </StatCard>
                    <StatCard color="#d4edda" textColor="#155724">
                        <div className="value">{stats.onPlatform}</div>
                        <div className="label">Sur la plateforme</div>
                    </StatCard>
                    <StatCard color="#e8f5e9" textColor="#2e7d32">
                        <div className="value">{stats.withProject}</div>
                        <div className="label">Projet lie</div>
                    </StatCard>
                    <StatCard color="#fff3cd" textColor="#856404">
                        <div className="value">{stats.tokensGenerated}</div>
                        <div className="label">Tokens generes</div>
                    </StatCard>
                    <StatCard color="#cce5ff" textColor="#004085">
                        <div className="value">{stats.signupsCompleted}</div>
                        <div className="label">Inscriptions via invitation</div>
                    </StatCard>
                    <StatCard color="#e0f2f1" textColor="#00695c">
                        <div className="value">{stats.projectsComplete}</div>
                        <div className="label">Projets complets</div>
                    </StatCard>
                </StatsSection>
            )}

            {/* Campaign summary */}
            {isCampaignTab && (
                <CampaignSummary>
                    <strong>Campagne Tier {activeTab.replace('tier', '')}</strong>
                    <span className="sep">|</span>
                    {getCampaignSummaryText(activeTab.replace('tier', ''))}
                </CampaignSummary>
            )}

            <TabsContainer>
                <Tab active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
                    Toutes ({allWinners.length})
                </Tab>
                {EDITION_IDS.map(id => (
                    <Tab
                        key={id}
                        active={activeTab === id}
                        onClick={() => setActiveTab(id)}
                    >
                        {EDITIONS[id].name} ({allWinners.filter(w => w.event_id === id).length})
                    </Tab>
                ))}
                <TabSeparator />
                <Tab active={activeTab === 'activity'} onClick={() => setActiveTab('activity')}>
                    Activite
                </Tab>
                <Tab active={activeTab === 'tierA'} onClick={() => setActiveTab('tierA')}>
                    Tier A ({allWinners.filter(w => w._tier === 'A').length})
                </Tab>
                <Tab active={activeTab === 'tierB'} onClick={() => setActiveTab('tierB')}>
                    Tier B ({allWinners.filter(w => w._tier === 'B').length})
                </Tab>
                <Tab active={activeTab === 'tierC'} onClick={() => setActiveTab('tierC')}>
                    Tier C ({allWinners.filter(w => w._tier === 'C').length})
                </Tab>
            </TabsContainer>

            {/* Activity Log View — simple table */}
            {isActivityTab && (() => {
                const { events, eventLabels } = getActivityEvents();
                return (
                    <TableSection>
                        <TableHeader>
                            <h2>Activite ({events.length})</h2>
                            <RefreshButton onClick={handleRefresh} disabled={refreshing}>
                                {refreshing ? 'Actualisation...' : '↻ Actualiser'}
                            </RefreshButton>
                        </TableHeader>
                        <div style={{ overflowX: 'auto' }}>
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>Nom</th>
                                        <th>Email</th>
                                        <th>Entreprise</th>
                                        <th>Detail</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {events.slice(0, 200).map((event, i) => (
                                        <tr key={i}>
                                            <td style={{ whiteSpace: 'nowrap' }}>{formatDate(event.date)}</td>
                                            <td>
                                                <StatusBadge className={event.type === 'account_created' || event.type === 'project_complete' ? 'onboarded' : 'pending'}>
                                                    {eventLabels[event.type]}
                                                </StatusBadge>
                                            </td>
                                            <td>{event.name || '-'}</td>
                                            <td>{event.email ? <a href={`mailto:${event.email}`} style={{ color: '#004085' }}>{event.email}</a> : '-'}</td>
                                            <td>{event.company || '-'}</td>
                                            <td style={{ color: '#888' }}>{event.detail || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        {events.length > 200 && <LastUpdate>{events.length} evenements (200 premiers affiches)</LastUpdate>}
                    </TableSection>
                );
            })()}

            {/* Table View (editions + campaign tabs) */}
            {!isActivityTab && <TableSection>
                <TableHeader>
                    <h2>Laureats ({filteredWinners.length})</h2>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <SearchInput
                            type="text"
                            placeholder="Rechercher par nom, entreprise, projet..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <RefreshButton onClick={handleRefresh} disabled={refreshing} style={{ marginLeft: 12 }}>
                            {refreshing ? 'Actualisation...' : '↻ Actualiser'}
                        </RefreshButton>
                        <ExportButton onClick={exportToCSV}>
                            ↓ Export CSV
                        </ExportButton>
                    </div>
                </TableHeader>

                <div style={{ overflowX: 'auto' }}>
                    <Table>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Projet</th>
                                <th>Entreprise</th>
                                <th>Createur</th>
                                <th>Email</th>
                                {(activeTab === 'all' || isCampaignTab) && <th>Edition</th>}
                                <th>Compte</th>
                                <th>Invitation</th>
                                <th>Correspondances</th>
                                <th>Projet Annuaire</th>
                                <th>Completude</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredWinners.map((winner, idx) => (
                                <tr key={winner.id}>
                                    <td style={{ color: '#999', fontSize: 12 }}>{idx + 1}</td>
                                    <td style={{ maxWidth: 240 }}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            {thumbnailMap[winner.project_id] && (
                                                <ProjectThumbnailSmall
                                                    style={{ backgroundImage: `url(${getProjectThumbnailUrl(thumbnailMap[winner.project_id])})` }}
                                                />
                                            )}
                                            <strong>{winner.project_name || '-'}</strong>
                                        </div>
                                    </td>
                                    <td style={{ maxWidth: 180 }}>
                                        {winner.company_name || '-'}
                                    </td>
                                    <td>
                                        {winner.first_name} {winner.last_name}
                                    </td>
                                    <td style={{ maxWidth: 200 }}>
                                        {winner.email ? (
                                            <a href={`mailto:${winner.email}`} style={{ color: '#004085' }}>
                                                {winner.email}
                                            </a>
                                        ) : '-'}
                                    </td>
                                    {(activeTab === 'all' || isCampaignTab) && (
                                        <td>
                                            <EditionBadge order={winner._edition?.order}>
                                                {winner._edition?.shortName || '-'}
                                            </EditionBadge>
                                        </td>
                                    )}
                                    <td>
                                        <StatusBadge className={winner.user_id ? 'onboarded' : 'pending'}>
                                            {winner.user_id ? '✓ Inscrit' : '○ En attente'}
                                        </StatusBadge>
                                    </td>
                                    <td>
                                        <InvitationBadge className={winner._invitationStatus}>
                                            {getInvitationLabel(winner._invitationStatus)}
                                        </InvitationBadge>
                                    </td>
                                    <td>
                                        {winner.user_id ? (
                                            <MatchBadge count={0}>Deja lie</MatchBadge>
                                        ) : winner.company_name ? (
                                            potentialMatchesCache[winner.id] ? (
                                                <MatchButton
                                                    hasMatches={potentialMatchesCache[winner.id].length > 0}
                                                    onClick={() => searchForMatches(winner)}
                                                >
                                                    {potentialMatchesCache[winner.id].length > 0
                                                        ? `${potentialMatchesCache[winner.id].length} trouve(s)`
                                                        : 'Aucun'}
                                                </MatchButton>
                                            ) : (
                                                <MatchButton onClick={() => searchForMatches(winner)}>
                                                    🔍 Chercher
                                                </MatchButton>
                                            )
                                        ) : (
                                            <MatchBadge count={0}>Pas d'entreprise</MatchBadge>
                                        )}
                                    </td>
                                    <td>
                                        {winner.project_id ? (
                                            <ProjectMatchBadge linked style={{ cursor: 'pointer' }} onClick={() => openProjectDetail(winner)}>
                                                ✓ Voir projet
                                            </ProjectMatchBadge>
                                        ) : winner.project_name ? (
                                            potentialProjectsCache[winner.id] ? (
                                                <ProjectMatchButton
                                                    hasMatches={potentialProjectsCache[winner.id].length > 0}
                                                    onClick={() => searchForProjects(winner)}
                                                >
                                                    {potentialProjectsCache[winner.id].length > 0
                                                        ? `${potentialProjectsCache[winner.id].length} trouve(s)`
                                                        : 'Aucun'}
                                                </ProjectMatchButton>
                                            ) : (
                                                <ProjectMatchButton onClick={() => searchForProjects(winner)}>
                                                    🔍 Chercher
                                                </ProjectMatchButton>
                                            )
                                        ) : (
                                            <ProjectMatchBadge>Pas de projet</ProjectMatchBadge>
                                        )}
                                    </td>
                                    <td style={{ cursor: winner.project_id ? 'pointer' : 'default' }} onClick={() => winner.project_id && openProjectDetail(winner)}>
                                        {winner.project_id ? (() => {
                                            const c = completenessMap[winner.project_id];
                                            if (!c) return <CompletenessBadge className="none">-</CompletenessBadge>;
                                            const allComplete = c.hasImage && c.hasDescription && c.hasTypology;
                                            if (allComplete) return <CompletenessBadge className="complete">✓ Complet</CompletenessBadge>;
                                            const missing = [];
                                            if (!c.hasImage) missing.push('Image');
                                            if (!c.hasDescription) missing.push('Desc.');
                                            if (!c.hasTypology) missing.push('Typo.');
                                            return (
                                                <div>
                                                    <CompletenessBadge className="partial">Incomplet</CompletenessBadge>
                                                    <CompletenessDetail>{missing.join(', ')}</CompletenessDetail>
                                                </div>
                                            );
                                        })() : (
                                            <CompletenessBadge className="none">-</CompletenessBadge>
                                        )}
                                    </td>
                                    <td>
                                        <ActionButton onClick={() => openEditModal(winner)}>
                                            ✎ Modifier
                                        </ActionButton>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>

                <LastUpdate>
                    Derniere actualisation : {lastUpdate?.toLocaleString('fr-FR')}
                </LastUpdate>
            </TableSection>}

            {/* Edit Modal */}
            {editModal && (
                <ModalOverlay onClick={closeEditModal}>
                    <ModalContent onClick={e => e.stopPropagation()}>
                        <ModalHeader>
                            <h3>Modifier le laureat</h3>
                            <CloseButton onClick={closeEditModal}>&times;</CloseButton>
                        </ModalHeader>

                        <FormGroup>
                            <label>Nom du projet</label>
                            <input
                                type="text"
                                value={editModal.project_name || ''}
                                onChange={(e) => handleEditChange('project_name', e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>Entreprise</label>
                            <input
                                type="text"
                                value={editModal.company_name || ''}
                                onChange={(e) => handleEditChange('company_name', e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>Prenom</label>
                            <input
                                type="text"
                                value={editModal.first_name || ''}
                                onChange={(e) => handleEditChange('first_name', e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>Nom</label>
                            <input
                                type="text"
                                value={editModal.last_name || ''}
                                onChange={(e) => handleEditChange('last_name', e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>Email</label>
                            <input
                                type="email"
                                value={editModal.email || ''}
                                onChange={(e) => handleEditChange('email', e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>Telephone</label>
                            <input
                                type="tel"
                                value={editModal.phone || ''}
                                onChange={(e) => handleEditChange('phone', e.target.value)}
                            />
                        </FormGroup>

                        {editModal._edition && (
                            <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>
                                Edition : {editModal._edition.name}
                            </div>
                        )}

                        <SaveButton onClick={handleSaveEdit} disabled={saving}>
                            {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                        </SaveButton>
                    </ModalContent>
                </ModalOverlay>
            )}

            {/* Company Match Modal */}
            {matchModal && (
                <ModalOverlay onClick={() => setMatchModal(null)}>
                    <MatchModalContent onClick={e => e.stopPropagation()}>
                        <ModalHeader>
                            <h3>Correspondances potentielles</h3>
                            <CloseButton onClick={() => setMatchModal(null)}>&times;</CloseButton>
                        </ModalHeader>

                        <MatchInfo>
                            <strong>Laureat recherche :</strong>
                            {matchModal.winner.first_name} {matchModal.winner.last_name} - {matchModal.winner.company_name}
                            <br />
                            <span style={{ fontSize: '12px', color: '#666' }}>
                                Email actuel : {matchModal.winner.email || 'Non renseigne'}
                            </span>
                        </MatchInfo>

                        {matchModal.searching ? (
                            <SearchingIndicator>
                                <span>Recherche en cours...</span>
                            </SearchingIndicator>
                        ) : matchModal.matches.length === 0 ? (
                            <NoMatchesMessage>
                                Aucune correspondance trouvee pour "{matchModal.winner.company_name}"
                            </NoMatchesMessage>
                        ) : (
                            <>
                                <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                                    {matchModal.matches.length} utilisateur(s) trouve(s)
                                </div>
                                <MatchList>
                                    {matchModal.matches.map(match => (
                                        <MatchCard
                                            key={match.user_id}
                                            selected={matchModal.selectedMatch?.user_id === match.user_id}
                                            onClick={() => handleSelectMatch(match)}
                                        >
                                            <div className="match-header">
                                                <div>
                                                    <div className="match-name">
                                                        {match.hsProfile?.firstname || match.first_name || ''} {match.hsProfile?.lastname || match.last_name || ''}
                                                    </div>
                                                    <div className="match-company">
                                                        {match.hsCompany?.name || match.hsProfile?.company || 'Entreprise inconnue'}
                                                    </div>
                                                </div>
                                                <div className="match-date">
                                                    Connexion : {formatDate(match.last_connection_date)}
                                                </div>
                                            </div>
                                            <div className="match-email">
                                                {match.email}
                                            </div>
                                        </MatchCard>
                                    ))}
                                </MatchList>

                                {matchModal.selectedMatch && (
                                    <SaveButton onClick={handleConfirmMatch} disabled={saving}>
                                        {saving ? 'Liaison en cours...' : `Lier a ${matchModal.selectedMatch.email}`}
                                    </SaveButton>
                                )}
                            </>
                        )}
                    </MatchModalContent>
                </ModalOverlay>
            )}

            {/* Project Match Modal */}
            {projectMatchModal && (
                <ModalOverlay onClick={() => setProjectMatchModal(null)}>
                    <MatchModalContent onClick={e => e.stopPropagation()}>
                        <ModalHeader>
                            <h3>Projets correspondants dans l'annuaire</h3>
                            <CloseButton onClick={() => setProjectMatchModal(null)}>&times;</CloseButton>
                        </ModalHeader>

                        <MatchInfo>
                            <strong>Projet recherche :</strong>
                            {projectMatchModal.winner.project_name}
                            <br />
                            <span style={{ fontSize: '12px', color: '#666' }}>
                                Entreprise : {projectMatchModal.winner.company_name || 'Non renseignee'}
                            </span>
                        </MatchInfo>

                        {projectMatchModal.searching ? (
                            <SearchingIndicator>
                                <span>Recherche en cours...</span>
                            </SearchingIndicator>
                        ) : projectMatchModal.matches.length === 0 ? (
                            <NoMatchesMessage>
                                Aucun projet correspondant trouve pour "{projectMatchModal.winner.project_name}"
                            </NoMatchesMessage>
                        ) : (
                            <>
                                <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                                    {projectMatchModal.matches.length} projet(s) trouve(s)
                                </div>
                                <MatchList>
                                    {projectMatchModal.matches.map(match => (
                                        <ProjectMatchCard
                                            key={match.project_id}
                                            selected={projectMatchModal.selectedMatch?.project_id === match.project_id}
                                            onClick={() => handleSelectProjectMatch(match)}
                                        >
                                            <div
                                                className="project-thumbnail"
                                                style={{
                                                    backgroundImage: match.thumbnail
                                                        ? `url(${getProjectThumbnailUrl(match.thumbnail)})`
                                                        : 'none'
                                                }}
                                            />
                                            <div className="project-info">
                                                <div className="project-name">
                                                    {match.project_name || match.project_name_en || 'Sans nom'}
                                                </div>
                                                {match.company_name && (
                                                    <div className="project-company">
                                                        {match.company_name}
                                                    </div>
                                                )}
                                                {match.type && (
                                                    <span className="project-type">{match.type}</span>
                                                )}
                                            </div>
                                        </ProjectMatchCard>
                                    ))}
                                </MatchList>

                                {projectMatchModal.selectedMatch && (
                                    <ConfirmProjectButton onClick={handleConfirmProjectMatch} disabled={saving}>
                                        {saving ? 'Liaison en cours...' : `Confirmer le projet "${projectMatchModal.selectedMatch.project_name}"`}
                                    </ConfirmProjectButton>
                                )}
                            </>
                        )}
                    </MatchModalContent>
                </ModalOverlay>
            )}

            {/* Project Detail Modal */}
            {projectDetailModal && (() => {
                const { winner, project, thumbnail } = projectDetailModal;
                const mediaBaseUrl = process.env.REACT_APP_MEDIA_BASE_URL;
                return (
                    <ModalOverlay onClick={() => setProjectDetailModal(null)}>
                        <MatchModalContent onClick={e => e.stopPropagation()}>
                            <ModalHeader>
                                <h3>Fiche projet</h3>
                                <CloseButton onClick={() => setProjectDetailModal(null)}>&times;</CloseButton>
                            </ModalHeader>

                            <MatchInfo>
                                <strong>{winner.project_name || project.name}</strong>
                                {winner.company_name} — {winner._edition?.name || ''}
                            </MatchInfo>

                            <div style={{ marginBottom: 16 }}>
                                <ProjectStatusDot ok={project.hasImage} /> Image
                                &nbsp;&nbsp;&nbsp;
                                <ProjectStatusDot ok={project.hasDescription} /> Description
                                &nbsp;&nbsp;&nbsp;
                                <ProjectStatusDot ok={project.hasTypology} /> Typologie
                            </div>

                            <ProjectDetailGrid>
                                <ProjectDetailField>
                                    <label>Nom du projet</label>
                                    <div className={project.name ? 'value' : 'empty'}>{project.name || 'Non renseigne'}</div>
                                </ProjectDetailField>
                                <ProjectDetailField>
                                    <label>Nom (EN)</label>
                                    <div className={project.nameEn ? 'value' : 'empty'}>{project.nameEn || 'Non renseigne'}</div>
                                </ProjectDetailField>
                                <ProjectDetailField>
                                    <label>Type</label>
                                    <div className={project.type ? 'value' : 'empty'}>{project.type || 'Non renseigne'}</div>
                                </ProjectDetailField>
                                <ProjectDetailField>
                                    <label>Typologie</label>
                                    <div className={project.typologie ? 'value' : 'empty'}>{project.typologie || 'Non renseigne'}</div>
                                </ProjectDetailField>
                                <ProjectDetailField>
                                    <label>Annee</label>
                                    <div className={project.year ? 'value' : 'empty'}>{project.year || 'Non renseigne'}</div>
                                </ProjectDetailField>
                                <ProjectDetailField>
                                    <label>Medias</label>
                                    <div className="value">{project.mediasCount} fichier{project.mediasCount !== 1 ? 's' : ''}</div>
                                </ProjectDetailField>
                                <ProjectDetailField className="full-width">
                                    <label>Description (FR)</label>
                                    <div className={project.aboutfr ? 'value' : 'empty'} style={{ fontSize: 13, maxHeight: 120, overflow: 'auto' }}>
                                        {project.aboutfr ? <span dangerouslySetInnerHTML={{ __html: project.aboutfr }} /> : 'Non renseigne'}
                                    </div>
                                </ProjectDetailField>
                                <ProjectDetailField className="full-width">
                                    <label>Description (EN)</label>
                                    <div className={project.abouten ? 'value' : 'empty'} style={{ fontSize: 13, maxHeight: 120, overflow: 'auto' }}>
                                        {project.abouten ? <span dangerouslySetInnerHTML={{ __html: project.abouten }} /> : 'Non renseigne'}
                                    </div>
                                </ProjectDetailField>
                            </ProjectDetailGrid>

                            {project.medias && project.medias.length > 0 && (
                                <ProjectDetailField>
                                    <label>Apercu medias</label>
                                    <ProjectDetailMedias>
                                        {project.medias.filter(m => m.type?.includes('image')).map((m, i) => (
                                            <img
                                                key={i}
                                                src={mediaBaseUrl + m.path}
                                                alt={m.credit || `Media ${i + 1}`}
                                                onClick={() => window.open(mediaBaseUrl + m.path, '_blank')}
                                            />
                                        ))}
                                    </ProjectDetailMedias>
                                </ProjectDetailField>
                            )}

                            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                                <a
                                    href={`/project?pid=${winner.project_id}&cid=${winner.company_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ flex: 1, textAlign: 'center', padding: '10px 16px', background: '#e7f3ff', color: '#004085', borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: 13 }}
                                >
                                    Voir page publique
                                </a>
                                <a
                                    href={`/myproject?id=${winner.project_id}&cid=${winner.company_id}&as=${btoa(winner.email)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ flex: 1, textAlign: 'center', padding: '10px 16px', background: '#fff3cd', color: '#856404', borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: 13 }}
                                >
                                    Modifier le projet
                                </a>
                            </div>
                        </MatchModalContent>
                    </ModalOverlay>
                );
            })()}
        </Container>
    );
};

export default FD100AllEditionsDashboard;
